import type { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import FormField from '@/components/FormField'
import Input from '@/components/Input/Input'
import {
  FormButtons,
  FormLayout,
  FormSection
} from '@/components/common/FormGrid/FormGrid'
import type { FormFieldType } from '@/types/form-field-type'

import DomainSelect from './DomainSelect'
import EducationLevelSelect from './EducationLevelSelect'
import FullNamePreview from './FullNamePreview'
import PreferredRoomsSelect from './PreferredRoomsSelect'
import type { CourseFormPayload } from '../constants/course-payload'
import { validateNameUniqueness } from '../utils/validate-name-uniqueness'

type CourseFormProps = {
  courseId?: string
  form: UseFormReturn<CourseFormPayload>
  onSubmit: (data: CourseFormPayload) => void
  educationLevelDisabled?: boolean
}

const CourseForm = (props: CourseFormProps) => {
  const { t } = useTranslation('coursesManagement')

  const nameState = props.form.getFieldState('name')
  const educationLevelState = props.form.getFieldState('educationLevel')

  const shouldValidateNameUniq =
    nameState.isDirty && educationLevelState.isDirty

  const triggerNameUniqValidation = async () => {
    if (!shouldValidateNameUniq) return
    await validateNameUniqueness({
      form: props.form,
      courseId: props.courseId
    })
  }

  const createChangeHandlerWithNameUniqValidation =
    <FieldValue,>(
      inputProps: FormFieldType<FieldValue>
    ): FormFieldType<FieldValue>['onChange'] =>
    (...args) => {
      inputProps.onChange?.(...args)
      triggerNameUniqValidation()
    }

  const createBlurHandlerWithNameUniqValidation =
    <FieldValue,>(
      inputProps: FormFieldType<FieldValue>
    ): FormFieldType<FieldValue>['onBlur'] =>
    (...args) => {
      inputProps.onBlur?.(...args)
      triggerNameUniqValidation()
    }

  const handleSubmit = async (data: CourseFormPayload) => {
    if (shouldValidateNameUniq) {
      const isValid = await validateNameUniqueness({
        form: props.form,
        courseId: props.courseId
      })
      if (!isValid) return
    }

    props.onSubmit(data)
  }

  return (
    <FormLayout form={props.form} onSubmit={handleSubmit}>
      <FormSection header={t('header.basic-information')}>
        <FormField
          control={props.form.control}
          id="educationLevel"
          label={t('label.education-level')}
          name="educationLevel"
          required
          labelTooltipText={
            props.educationLevelDisabled
              ? t('tooltip.education-level-disabled')
              : undefined
          }
          render={({ inputProps }) => (
            <EducationLevelSelect
              {...inputProps}
              disabled={props.educationLevelDisabled}
              onChange={createChangeHandlerWithNameUniqValidation(inputProps)}
              onBlur={createBlurHandlerWithNameUniqValidation(inputProps)}
            />
          )}
        />
        <FormField
          control={props.form.control}
          id="name"
          name="name"
          required
          label={t('label.course-name')}
          labelTooltipText={t('tooltip.name-should-be-unique')}
          render={({ inputProps }) => (
            <Input
              maxLength={80}
              {...inputProps}
              onChange={createChangeHandlerWithNameUniqValidation(inputProps)}
              onBlur={createBlurHandlerWithNameUniqValidation(inputProps)}
            />
          )}
        />
        <FullNamePreview />
        <FormField
          control={props.form.control}
          id="domain"
          label={t('label.domain')}
          name="domain"
          required
          render={({ inputProps }) => <DomainSelect {...inputProps} />}
        />
        <FormField
          control={props.form.control}
          id="preferredRooms"
          label={t('label.preferred-rooms')}
          name="preferredRooms"
          render={({ inputProps }) => <PreferredRoomsSelect {...inputProps} />}
        />
      </FormSection>
      <FormButtons />
    </FormLayout>
  )
}

export default CourseForm
